import React, { useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { useOrgs, useMaterials, useIncidents } from '../../hooks/useLibraryData';
import { getRolesForOrg } from './roleMap';
import './Library.css';

// All role categories
const allRoles = [
  'Political Lobbying', 'Media & Communications', 'Campus & Youth',
  'Communal Bodies', 'Policy & Research', 'Fundraising & Philanthropy',
  'State Institution', 'Surveillance & Monitoring', 'Settlement & Colonisation',
  'Cultural Programming', 'Christian Zionism', 'Legal', 'Trade & Business', 'Security'
];

// All level categories
const allLevels = ['Local', 'National', 'International'];

// Filter toggle button
function FilterButton({ label, count, isOpen, onClick }) {
  const hasSelection = count > 0;
  return (
    <button
      className={`hf-dropdown-btn ${hasSelection ? 'has-selection' : ''} ${isOpen ? 'is-open' : ''}`}
      onClick={onClick}
    >
      {label} {count > 0 && `(${count})`} <span className="hf-arrow"></span>
    </button>
  );
}

// Inline expand panel
function ExpandPanel({ options, selected, onToggle }) {
  return (
    <div className="hf-expand-panel">
      {options.map(opt => (
        <span
          key={opt}
          className={`hf-panel-option ${selected.includes(opt) ? 'selected' : ''}`}
          onClick={() => onToggle(opt)}
        >
          {opt}
        </span>
      ))}
    </div>
  );
}

// Highlight search terms in text
function highlightText(text, query) {
  if (!text || !query.trim()) return text;
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return text;
  const escaped = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="search-highlight">{part}</mark>
      : part
  );
}

// Score an org's relevance to a search query
function scoreOrgRelevance(org, words, orgRoles) {
  let score = 0;
  const name = (org.name || '').toLowerCase();
  const desc = (org.description || '').toLowerCase();
  const country = (org.country || '').toLowerCase();
  const rolesStr = orgRoles.join(' ').toLowerCase();

  for (const w of words) {
    if (name.includes(w)) score += 100;
    if (country.includes(w)) score += 30;
    if (rolesStr.includes(w)) score += 20;
    if (desc.includes(w)) score += 5;
  }
  return score;
}

export default function OrganisationsPage() {
  const { orgs: orgsData } = useOrgs();
  const { materials: materialsData } = useMaterials();
  const { incidents: incidentsDataAll } = useIncidents();

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilters, setCountryFilters] = useState([]);
  const [roleFilters, setRoleFilters] = useState([]);
  const [levelFilters, setLevelFilters] = useState([]);
  const [openPanel, setOpenPanel] = useState(null);
  const togglePanel = (name) => setOpenPanel(prev => prev === name ? null : name);

  // Helper functions
  const getOrgById = useCallback((id) => orgsData.find(org => org.id === id), [orgsData]);
  const getMaterialsForOrg = useCallback((orgId) => materialsData.filter(m => m.orgId === orgId), [materialsData]);
  const getIncidentsForOrg = useCallback((orgId) => {
    return incidentsDataAll.filter(i =>
      i.relatedOrgs && (Array.isArray(i.relatedOrgs) ? i.relatedOrgs.includes(orgId) : false)
    );
  }, [incidentsDataAll]);

  // Country display order
  const countryOrder = ['Israeli state', 'United States', 'United Kingdom', 'Canada', 'Germany', 'Australia'];

  const countries = useMemo(() => {
    const countrySet = new Set();
    orgsData.forEach(org => {
      if (org.country) countrySet.add(org.country);
    });
    return Array.from(countrySet).sort((a, b) => {
      const aIdx = countryOrder.indexOf(a);
      const bIdx = countryOrder.indexOf(b);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [orgsData]);

  // Toggle helpers
  const toggleCountry = (c) => setCountryFilters(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  const toggleRole = (r) => setRoleFilters(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  const toggleLevel = (l) => setLevelFilters(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);

  // Filter organisations
  const isSearching = searchQuery.trim() !== '';
  const filteredOrganisations = useMemo(() => {
    let results = [...orgsData];
    const words = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);

    if (words.length > 0) {
      results = results.filter(org => {
        const name = (org.name || '').toLowerCase();
        const acronym = (org.acronym || '').toLowerCase();
        const desc = (org.description || '').toLowerCase();
        const country = (org.country || '').toLowerCase();
        const roles = getRolesForOrg(org.id, org.roles).join(' ').toLowerCase();
        const scope = (org.scope || '').toLowerCase();
        const haystack = `${name} ${acronym} ${desc} ${country} ${roles} ${scope}`;
        return words.every(w => haystack.includes(w));
      });
    }
    if (countryFilters.length > 0) {
      results = results.filter(org => countryFilters.includes(org.country));
    }
    if (roleFilters.length > 0) {
      results = results.filter(org => {
        const orgRoles = getRolesForOrg(org.id, org.roles);
        return orgRoles.some(r => roleFilters.includes(r));
      });
    }
    if (levelFilters.length > 0) {
      results = results.filter(org => levelFilters.includes(org.scope));
    }

    if (words.length > 0) {
      results.sort((a, b) => {
        const aScore = scoreOrgRelevance(a, words, getRolesForOrg(a.id, a.roles));
        const bScore = scoreOrgRelevance(b, words, getRolesForOrg(b.id, b.roles));
        if (bScore !== aScore) return bScore - aScore;
        return (a.name || '').localeCompare(b.name || '');
      });
    } else {
      const scopePriority = { 'International': 0, 'National': 1, 'Local': 2 };
      results.sort((a, b) => {
        const aPri = scopePriority[a.scope] !== undefined ? scopePriority[a.scope] : 4;
        const bPri = scopePriority[b.scope] !== undefined ? scopePriority[b.scope] : 4;
        if (aPri !== bPri) return aPri - bPri;
        return (a.name || '').localeCompare(b.name || '');
      });
    }
    return results;
  }, [searchQuery, countryFilters, roleFilters, levelFilters, orgsData]);

  // Group organisations by country
  const groupedOrganisations = useMemo(() => {
    const groups = {};
    filteredOrganisations.forEach(org => {
      const country = org.country || 'Other';
      if (!groups[country]) groups[country] = [];
      groups[country].push(org);
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      const aIdx = countryOrder.indexOf(a);
      const bIdx = countryOrder.indexOf(b);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.localeCompare(b);
    });
    return sortedKeys.map(country => ({ country, orgs: groups[country] }));
  }, [filteredOrganisations]);

  // Stats
  const totalOrganisations = orgsData.length;
  const totalCountries = new Set(orgsData.map(org => org.country)).size;
  const hasFilters = countryFilters.length > 0 || roleFilters.length > 0 || levelFilters.length > 0 || searchQuery.trim() !== '';

  const handleClearAll = () => {
    setCountryFilters([]);
    setRoleFilters([]);
    setLevelFilters([]);
    setSearchQuery('');
  };

  return (
    <>
      <Helmet>
        <title>Organisations - Hasbara Tracker</title>
        <meta name='description' content='Browse documented pro-Israel advocacy organisations tracked by Hasbara Tracker' />
      </Helmet>

      <Header />

      <main className='library-page'>
        {/* Page Header */}
        <section className='library-header'>
          <h1 className='library-page-title'>Organisations</h1>
          <p className='library-intro'>
            Pro-Israel advocacy organisations documented by Hasbara Tracker, including lobbying groups, media watchdogs, state institutions, and communal bodies operating across multiple countries.
          </p>
          <p className='library-stats-line'>
            {totalOrganisations} organisations across {totalCountries} countries
          </p>
        </section>

        {/* Search bar */}
        <input
          type='text'
          placeholder='Search organisations...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='hf-search'
        />

        {/* Filter bar */}
        <div className='hf-filter-bar'>
          <div className='hf-button-row'>
            <FilterButton label='Location' count={countryFilters.length} isOpen={openPanel === 'location'} onClick={() => togglePanel('location')} />
            <FilterButton label='Role' count={roleFilters.length} isOpen={openPanel === 'role'} onClick={() => togglePanel('role')} />
            <FilterButton label='Level' count={levelFilters.length} isOpen={openPanel === 'level'} onClick={() => togglePanel('level')} />
            {hasFilters && <button className='hf-clear-all' onClick={handleClearAll}>Clear all</button>}
          </div>
          {openPanel === 'location' && <ExpandPanel options={countries} selected={countryFilters} onToggle={toggleCountry} />}
          {openPanel === 'role' && <ExpandPanel options={allRoles} selected={roleFilters} onToggle={toggleRole} />}
          {openPanel === 'level' && <ExpandPanel options={allLevels} selected={levelFilters} onToggle={toggleLevel} />}
        </div>

        <p className='library-results-count'>
          {filteredOrganisations.length} organisation{filteredOrganisations.length !== 1 ? 's' : ''}
          {hasFilters ? ` (filtered from ${totalOrganisations})` : ''}
        </p>

        {/* Organisations List */}
        <div className='library-items-list'>
          {filteredOrganisations.length > 0 ? (
            isSearching ? (
              /* Flat relevance-sorted list when searching */
              filteredOrganisations.map(org => {
                const materialsCount = getMaterialsForOrg(org.id).length;
                const incidentsCount = getIncidentsForOrg(org.id).length;
                const parentOrg = org.parentOrg ? getOrgById(org.parentOrg) : null;
                return (
                  <Link key={org.slug} to={`/organisations/${org.slug}`} className='library-org-item'>
                    <div className='library-item-content'>
                      <div className='library-org-header'>
                        <h3 className='library-item-title'>
                          {highlightText(org.name, searchQuery)}
                          {org.acronym && <span className='library-org-acronym'> ({org.acronym})</span>}
                        </h3>
                      </div>
                      {getRolesForOrg(org.id, org.roles).length > 0 && (
                        <div className='library-org-domains'>
                          {getRolesForOrg(org.id, org.roles).map((domain, idx) => (
                            <React.Fragment key={domain}>
                              {idx > 0 && <span className='library-org-domain-sep'>/</span>}
                              <span className='library-org-domain'>{highlightText(domain, searchQuery)}</span>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                      {org.description && <p className='library-item-description'>{highlightText(org.description, searchQuery)}</p>}
                      <div className='library-org-stats'>
                        {parentOrg && (
                          <>
                            <span className='library-org-parent'>Part of {parentOrg.name}</span>
                            <span className='library-org-stat-separator'>·</span>
                          </>
                        )}
                        <span className='library-org-stat'>
                          {materialsCount} material{materialsCount !== 1 ? 's' : ''}
                        </span>
                        <span className='library-org-stat-separator'>·</span>
                        <span className='library-org-stat'>
                          {incidentsCount} incident{incidentsCount !== 1 ? 's' : ''}
                        </span>
                        <span className='library-org-footer-right'>
                          {org.country && (
                            <>
                              <span className='library-org-founded'>{org.country}</span>
                              <span className='library-org-stat-separator'>·</span>
                            </>
                          )}
                          {org.scope && (
                            <span className='library-org-operations'>Level | {org.scope}</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })
            ) : (
              /* Country-grouped list when browsing */
              groupedOrganisations.map(group => (
                <div key={group.country} className='library-country-group'>
                  <div className='library-country-header'>
                    <h2 className='library-country-title'>{group.country}</h2>
                    <span className='library-country-count'>{group.orgs.length}</span>
                  </div>
                  {group.orgs.map(org => {
                    const materialsCount = getMaterialsForOrg(org.id).length;
                    const incidentsCount = getIncidentsForOrg(org.id).length;
                    const parentOrg = org.parentOrg ? getOrgById(org.parentOrg) : null;
                    return (
                      <Link key={org.slug} to={`/organisations/${org.slug}`} className='library-org-item'>
                        <div className='library-item-content'>
                          <div className='library-org-header'>
                            <h3 className='library-item-title'>
                              {org.name}
                              {org.acronym && <span className='library-org-acronym'> ({org.acronym})</span>}
                            </h3>
                          </div>
                          {getRolesForOrg(org.id, org.roles).length > 0 && (
                            <div className='library-org-domains'>
                              {getRolesForOrg(org.id, org.roles).map((domain, idx) => (
                                <React.Fragment key={domain}>
                                  {idx > 0 && <span className='library-org-domain-sep'>/</span>}
                                  <span className='library-org-domain'>{domain}</span>
                                </React.Fragment>
                              ))}
                            </div>
                          )}
                          {org.description && <p className='library-item-description'>{org.description}</p>}
                          <div className='library-org-stats'>
                            {parentOrg && (
                              <>
                                <span className='library-org-parent'>Part of {parentOrg.name}</span>
                                <span className='library-org-stat-separator'>·</span>
                              </>
                            )}
                            <span className='library-org-stat'>
                              {materialsCount} material{materialsCount !== 1 ? 's' : ''}
                            </span>
                            <span className='library-org-stat-separator'>·</span>
                            <span className='library-org-stat'>
                              {incidentsCount} incident{incidentsCount !== 1 ? 's' : ''}
                            </span>
                            <span className='library-org-footer-right'>
                              {org.founded && (
                                <>
                                  <span className='library-org-founded'>{org.founded}</span>
                                  {org.scope && <span className='library-org-stat-separator'>·</span>}
                                </>
                              )}
                              {org.scope && (
                                <span className='library-org-operations'>Level | {org.scope}</span>
                              )}
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ))
            )
          ) : (
            <div className='library-no-results'><p>No organisations found matching your filters.</p></div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}
